import React, { useCallback, useRef, useState, useMemo } from "react";
import { Button, RadioGroup, Callout, Intent, Radio } from "@blueprintjs/core";
import useAuthService from "../../services/authService";
import ITenantInfo from "../../models/TenantInfo";
import { getCurrentUserPrefs } from "../../services/localPrefsService";
import styles from "./auth.module.scss";

const ErrorMessage = ({ message }: { message?: string }) => message
  ? <Callout intent={Intent.DANGER} icon={null}>{message}</Callout>
  : null;



interface ITenantListProps {
  tenants: ITenantInfo[] | null;
  selectedTenant?: string;
  setSelectedTenant: (tenantId: string) => void;
  currentUserEmail?: string;
}


const TenantList = ({ tenants, selectedTenant, setSelectedTenant, currentUserEmail }: ITenantListProps) => {
  const initialSelectedTenantId = useRef(selectedTenant);

  const setInitialTenantSelectionRef = useCallback((radioValue: string) => {
    if (radioValue === initialSelectedTenantId.current) {
      setTimeout(() => {
        const element = document.querySelector(`input[value="${radioValue}"]`);
        if (element) {
          element.scrollIntoView({ block: "center" });
        }
      }, 0);
    }
  }, []);


  const onTenantSelected = useCallback((e: React.FormEvent<HTMLInputElement>) => {
    setSelectedTenant(e.currentTarget.value);
  }, [setSelectedTenant]);

  if (tenants === null) {
    return <ErrorMessage message="Failed to load database list!" />
  }

  if (tenants.length === 0) {
    return (
      <Callout intent={Intent.DANGER} title="Not Available">
        {`Your user account (${currentUserEmail}) currently doesn't have access to any databases!`}
      </Callout>);
  }

  return (
    <RadioGroup onChange={onTenantSelected} selectedValue={selectedTenant}>
      {tenants.map(t => {
        const isSelected = t.RecordId === selectedTenant;
        setInitialTenantSelectionRef(t.RecordId);
        return (
          <Radio key={t.RecordId} value={t.RecordId} label={t.InstallationName} checked={isSelected} />
        );
      })}
    </RadioGroup>
  );
}


interface ITenantSelectionDialogProps {
  tenants: ITenantInfo[] | null;
  onTenantSelected: (tenant: ITenantInfo) => void;
  currentUserEmail?: string;
}


const TenantSelectionDialog: React.FC<ITenantSelectionDialogProps> = (props) => {
  const lastSelectedTenantId = getCurrentUserPrefs(props.currentUserEmail)?.tenantInstallationId;
  const [selectedTenant, setSelectedTenant] = useState<string | undefined>(lastSelectedTenantId);
  const haveTenants = (props.tenants && props.tenants.length > 0) || false;
  const authService = useAuthService();
  const onLogout = useCallback(async () => authService.logout(() => (window.location.href = "/")), [authService]);

  const onTenantSelected = useCallback(() => {
    const tenant = props.tenants!.find(t => t.RecordId === selectedTenant);
    console.assert(tenant);
    props.onTenantSelected(tenant!);
  }, [props, selectedTenant]);


  const footer = useMemo(() => {
    const noDbAccess = props.tenants && !props.tenants.length;
    return (
      <>
        {haveTenants && <Button large disabled={!selectedTenant} onClick={onTenantSelected} className={styles.acceptButton} text="GET STARTED" />}
        {noDbAccess && <Button large onClick={onLogout} className={styles.acceptButton} text="LOGOUT" />}
      </>
    )
  }, [haveTenants, onLogout, onTenantSelected, props.tenants, selectedTenant]);


  return (
    <div className={styles.tenantSelectionContainer}>
      <div className={styles.tenantSelectionCard}>
        <div className={styles.tenantSelectionCardHeader}>
          <p className={styles.tenantSelectionLabel}>Choose your database:</p>
        </div>
        <div className={styles.tenantSelectionList}>
          <TenantList tenants={props.tenants} selectedTenant={selectedTenant} setSelectedTenant={setSelectedTenant} currentUserEmail={props.currentUserEmail} />
        </div>
      </div>
      <div className={styles.tenantSelectionFooter}>
        {footer}
      </div>
    </div>
  );
}

export default TenantSelectionDialog;
